import { clsx } from 'clsx'
import { BookCheck, Check, ClipboardCheck, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { coachingOperationAppearance } from './coachingOperationAppearance'
import type { ComponentStatus } from '@/types/coaching'

export type OperationStepKey = 'reviewNote' | 'understanding' | 'workbook' | 'lessonPhoto'

export interface OperationStep {
  key: OperationStepKey
  status: Exclude<ComponentStatus, 'skipped'>
  statusLabel?: string
  needsGrading?: boolean
}

interface Props {
  step: OperationStep
  index: number
  selected: boolean
  onSelect: (key: OperationStepKey) => void
  collapsed?: boolean
}

export function OperationStepListItem({ step, index, selected, onSelect, collapsed = false }: Props) {
  const { t } = useTranslation('coaching')
  const appearance = step.key === 'reviewNote' || step.key === 'lessonPhoto'
    ? coachingOperationAppearance[step.key]
    : null
  const Icon = appearance?.Icon ?? (step.key === 'understanding' ? ClipboardCheck : BookCheck)
  const statusLabel = step.statusLabel ?? t(`unit.list.status.${step.status}`)
  const label = t(`operationStep.${step.key}`)

  if (collapsed) {
    return (
      <button
        type="button"
        aria-current={selected ? 'step' : undefined}
        aria-label={`${index + 1}. ${label}, ${statusLabel}${step.needsGrading ? `, ${t('unit.list.needsGrading')}` : ''}`}
        title={`${index + 1}. ${label}`}
        data-compact="true"
        onClick={() => onSelect(step.key)}
        className={clsx(
          'relative flex size-10 items-center justify-center rounded-lg border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          selected ? 'border-primary bg-accent text-accent-foreground' : 'border-transparent hover:bg-muted',
        )}
      >
        <span className="absolute left-1 top-0.5 text-[9px] font-semibold tabular-nums text-muted-foreground">
          {index + 1}
        </span>
        <Icon className={clsx('size-4', appearance?.iconText)} aria-hidden />
        {step.needsGrading ? (
          <span
            className="absolute right-1 top-1 size-2 rounded-full bg-destructive ring-2 ring-background"
            aria-hidden
          />
        ) : null}
      </button>
    )
  }

  return (
    <button
      type="button"
      aria-current={selected ? 'step' : undefined}
      onClick={() => onSelect(step.key)}
      className={clsx(
        'flex min-w-0 items-center gap-3 rounded-lg border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
        selected ? 'border-primary-600 bg-primary-50' : 'border-transparent hover:bg-gray-50',
      )}
    >
      <span className="w-5 text-right text-sm tabular-nums text-gray-400">{index + 1}</span>
      <Icon
        className={clsx(
          'h-5 w-5 shrink-0',
          appearance?.iconText,
          step.key === 'understanding' && 'text-indigo-500',
          step.key === 'workbook' && 'text-emerald-600',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
        {label}
      </span>
      <span className={clsx(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold',
        step.status === 'completed' && 'bg-green-50 text-green-700',
        step.status === 'in_progress' && 'bg-blue-50 text-blue-700',
        step.status === 'not_started' && 'bg-gray-100 text-gray-600',
      )}>
        {step.status === 'completed' ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <Play className="h-3.5 w-3.5" fill="currentColor" />}
        {statusLabel}
      </span>
    </button>
  )
}
